import React, { useMemo, useState, useSyncExternalStore } from "react";

/**
 * On-device push diagnostic overlay.
 *
 * Renders only when `?debugPush=1` is in the URL OR localStorage has
 * `fd_debug_push=1`. Subscribes to `push-debug` CustomEvents dispatched by
 * pushNotifications.js and shows the last 12 stages with timestamps.
 *
 * Usage from a native app: append `?debugPush=1` to the WebView URL or run
 * `localStorage.setItem('fd_debug_push','1')` in Safari Web Inspector once,
 * reopen the app, and you'll see exactly where registration is failing.
 */

// Module-level ring buffer (persists across re-mounts so events captured
// before the overlay mounted are still visible).
const _events = [];
const _listeners = new Set();
if (typeof window !== "undefined") {
  window.addEventListener("push-debug", (e) => {
    const d = e?.detail || {};
    _events.push({ ...d, at: new Date().toLocaleTimeString() });
    if (_events.length > 12) _events.shift();
    _listeners.forEach((l) => l());
  });
}

function subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}
function getSnapshot() { return _events.length; }   // primitive snapshot is fine
function getServerSnapshot() { return 0; }

export default function PushDebugOverlay() {
  // Derive enabled flag synchronously — no setState in effect.
  const enabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const url = new URL(window.location.href);
      const fromUrl = url.searchParams.get("debugPush") === "1";
      if (fromUrl) {
        try { localStorage.setItem("fd_debug_push", "1"); } catch { /* private mode */ }
        return true;
      }
      return localStorage.getItem("fd_debug_push") === "1";
    } catch { return false; }
  }, []);

  // Force re-render whenever the global event ring buffer changes.
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [dismissed, setDismissed] = useState(false);

  if (!enabled || dismissed) return null;

  const dismiss = () => {
    try { localStorage.removeItem("fd_debug_push"); } catch { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="push-debug-overlay"
      style={{
        position: "fixed", bottom: 12, right: 12, zIndex: 99999,
        maxWidth: 360, maxHeight: "60vh", overflow: "auto",
        background: "rgba(20, 20, 24, 0.92)", color: "#fff",
        borderRadius: 12, padding: "10px 12px",
        fontSize: 11, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        boxShadow: "0 10px 30px rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)",
      }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <strong>Push debug</strong>
        <button
          onClick={dismiss}
          style={{ background: "transparent", color: "#bbb", border: "1px solid #444", borderRadius: 6, fontSize: 10, padding: "2px 6px", cursor: "pointer" }}>
          dismiss
        </button>
      </div>
      {_events.length === 0 ? (
        <div style={{ opacity: 0.6 }}>Waiting for push events…</div>
      ) : (
        _events.map((e, i) => (
          <div key={i} style={{ borderTop: i ? "1px solid rgba(255,255,255,0.08)" : "none", paddingTop: 4, marginTop: 4 }}>
            <div><span style={{ color: "#9ad" }}>{e.at}</span> <strong>{e.stage}</strong></div>
            {e.detail && (
              <pre style={{ margin: "2px 0 0 0", whiteSpace: "pre-wrap", wordBreak: "break-all", color: "#cdd", fontSize: 10 }}>
                {typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail, null, 0)}
              </pre>
            )}
          </div>
        ))
      )}
    </div>
  );
}
